import { effect, Signal, untracked } from '@angular/core';

type SignalInputTuple<T> = {
  [K in keyof T]: Signal<T[K]>;
};

type SignalsToWatch<F, T extends readonly unknown[]> = [Signal<F>, ...SignalInputTuple<T>];

type ResultFn<F, T extends readonly unknown[]> = ((...values: Partial<[F, ...T]>) => void) | (() => void);

/**
 * Helper to simplify the usage of effects with signals in a "safe"/more explicit way.
 *
 * The first params are the signals you want to listen to, followed by a function that is called inside an "untracked"
 * block every time any of the signals change.
 *
 * Usage:
 * ```
 * const numberSignal = signal(123);
 * const stringSignal = signal('hello');
 *
 * explicitEffect(numberSignal, value => console.log(value));
 * explicitEffect(numberSignal, stringSignal, (numberValue, stringValue) => console.log(numberValue, stringValue));
 * ```
 */
export function explicitEffect<T extends readonly unknown[], F>(signalsToWatch: SignalsToWatch<F, T>, fn: ResultFn<F, T>): void;
export function explicitEffect<T extends readonly unknown[], F>(...signalsAndFunction: [...SignalsToWatch<F, T>, ResultFn<F, T>]): void;
export function explicitEffect<T>(...args: unknown[]): void {
  const fn = args.pop() as (...values: T[]) => void;
  const signalsToWatch = args as Signal<T>[];

  effect(() => {
    const signals = Array.isArray(signalsToWatch[0]) ? signalsToWatch[0] : signalsToWatch;
    const values = signals.map(sig => sig());

    untracked(() => fn(...values));
  });
}
